import React, { useMemo, useState } from 'react';
import dynamic from 'next/dynamic';
import { Box } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import LoadingSpinner from 'views/common/components/Logical/Layout/LoadingSpinner';

import theme from 'lib/config/theme';
import { useSelector } from 'react-redux';
import useAppNavigator from 'views/rebranded-module/common/hooks/useAppNavigator';
import Head from 'next/head';
import {
  FooterV2,
  FooterV2Skeleton,
  NavBar,
} from 'views/rebranded-module/common/components/Logical/LayoutV2';
import { SnackBarV2 } from 'views/rebranded-module/common/components';
import useLayoutHeights from 'views/rebranded-module/common/hooks/UseLayoutHeight';
import { LazyLoad } from 'views/common/components';
import Revzilla from 'views/common/components/Logical/Layout/Footer/Revzilla';
import Cookies from 'views/common/components/Logical/Layout/Footer/Cookies';
import { AppAdMobileV2 } from 'views/rebranded-module/common/components/UI/AppAdMobileV2';

const PendingReservationsV2 = dynamic(() => import('views/rebranded-module/common/components').then(
  (file) => file.PendingReservationsV2,
));

const ErrorPageV2 = dynamic(() => import('views/rebranded-module/common/components').then(
  (file) => file.ErrorPageV2,
));
const CaptureEmailOverlayV2 = dynamic(() => import(
  'views/rebranded-module/common/components/Logical/CaptureEmailOverlayV2'
).then((file) => file.CaptureEmailOverlayV2));

const { envVars } = require('next/config').default().publicRuntimeConfig;

const { GTM_CODE, HUBSPOT_CODE, GTG_PATH } = envVars;

// Google tag gateway: when GTG_PATH is set, load GTM through our own domain
// (CloudFront proxies GTG_PATH* to Google). Falls back to Google's domain otherwise.
const GTM_LOADER_URL = GTG_PATH
  ? `${GTG_PATH}/?id=`
  : 'https://www.googletagmanager.com/gtm.js?id=';

const LayoutV2 = (props) => {
  const { children, options = {} } = props;
  const {
    showCaptureEmailOverlay = true,
    showFooter = true,
    showCookieNotification = true,
    showPendingReservationMessage = true,
    skipLayoutFromGlobal = false,
    showLayout = true,
    transparentNavBar,
    simpleNavBar,
    reservationPath,
    staticNavBar,
    showGeoFilters,
  } = options;
  const appNavigator = useAppNavigator();
  const {
    appState, isCrawler, isSSR, isInIframe,
  } = useSelector(
    (state) => state.globalData,
  );
  const error = useSelector((state) => state.ui.error);
  const [isAdVisible, setAdVisible] = useState(true);
  const [adHeight, setAdHeight] = useState(0);
  const { LAYOUT_HEIGHT, LAYOUT_HEIGHT_SM } = useLayoutHeights();
  const hideLayout = useMemo(
    () => typeof ReactNativeWebView !== 'undefined'
      || appNavigator.query.hideLayout === '1'
      || isInIframe
      || !showLayout,
    [],
  );
  const renderTopLayout = () => {
    const topLayout = (
      <NavBar
        isAdVisible={isAdVisible}
        simple={simpleNavBar}
        transparent={transparentNavBar}
        staticPosition={staticNavBar}
        reservationPath={reservationPath}
        adHeight={adHeight}
        showGeoFilters={showGeoFilters}
      />
    );
    if (isCrawler) {
      return topLayout;
    }

    if (!isSSR && typeof window === 'undefined') return null;
    if (typeof ReactNativeWebView === 'undefined') return topLayout;

    return null;
  };

  return (
    <ThemeProvider theme={theme}>
      <Box className="LayoutContainer">
        <Head>
          {Object.keys(appState).length > 0
            && !appState.current_user?.privileged
            && !isCrawler && (
              <>
                <script
                  defer
                  dangerouslySetInnerHTML={{
                    __html: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
          new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
          j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
          '${GTM_LOADER_URL}'+i+dl;f.parentNode.insertBefore(j,f);
          })(window,document,'script','dataLayer','${GTM_CODE}');`,
                  }}
                />
                <script
                  defer
                  src={`https://www.googletagmanager.com/gtag/js?id=${GTM_CODE}`}
                />
                <script
                  dangerouslySetInnerHTML={{
                    __html: `window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config', '${GTM_CODE}');`,
                  }}
                />

                <script
                  type="text/javascript"
                  id="hs-script-loader"
                  async
                  defer
                  src={`//js.hs-scripts.com/${HUBSPOT_CODE}.js`}
                />
              </>
          )}
        </Head>
        <Box className="showTABLET">
          {!isCrawler
            && typeof ReactNativeWebView === 'undefined'
            && !isInIframe && (
              <AppAdMobileV2
                localStorageName="hide_mobile_app_ad"
                localStorageValue={1}
                setAdVisible={setAdVisible}
                isAdVisible={isAdVisible}
                setAdHeight={setAdHeight}
              />
          )}
        </Box>

        <LoadingSpinner />
        <SnackBarV2 />
        {!hideLayout && renderTopLayout()}
        {appState?.current_reservation
          && appState.current_reservation.state
          && showPendingReservationMessage && <PendingReservationsV2 />}
        <main>
          {error.statusCode && (
            <ErrorPageV2
              statusCode={error.statusCode}
              title={error.title}
              subtitle={error.subtitle}
            />
          )}
          <Box
            hidden={error.statusCode}
            mt={{
              lg: transparentNavBar ? 0 : LAYOUT_HEIGHT,
              sm: transparentNavBar ? 0 : LAYOUT_HEIGHT_SM,
            }}>
            {skipLayoutFromGlobal
              ? children
              : Object.keys(appState).length > 0 && children}
          </Box>
        </main>
        {showFooter && Object.keys(appState).length > 0 && !hideLayout && (
          <LazyLoad skeleton={<FooterV2Skeleton />} dataLoaded>
            <Box className="FooterContainer">
              <FooterV2 showCookieNotification={showCookieNotification} />
            </Box>
          </LazyLoad>
        )}
        {appState?.settings?.show_revzilla && <Revzilla />}
        {appState?.settings?.show_cookie_notification
          && showCookieNotification && <Cookies />}
        {!isCrawler
          && showCaptureEmailOverlay
          && !appState.current_user
          && appState?.settings?.show_capture_email_overlay.enabled
          && typeof ReactNativeWebView === 'undefined'
          && !isInIframe && <CaptureEmailOverlayV2 />}
        {appState.structured_data && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(appState.structured_data),
            }}
          />
        )}
      </Box>
    </ThemeProvider>
  );
};

export { LayoutV2 };
